import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Moon, Sun, Building } from 'lucide-react';
import DokumenteTab from '@/components/DokumenteTab';
import BenutzerTab from '@/components/BenutzerTab';
import WidgetConfigTab from '@/components/WidgetConfigTab';
import SitemapTab from '@/components/SitemapTab';
import LandesrechtTab from '@/components/LandesrechtTab';
import MonitoringTab from '@/components/MonitoringTab';
import TestumgebungTab from '@/components/TestumgebungTab';
import QualitaetTab from '@/components/QualitaetTab';
import GemeindenTab from '@/components/GemeindenTab';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import apiClient from '@/lib/apiClient';
import { getUserFromToken } from '@/lib/jwt';
import useInactivityLogout from '@/hooks/useInactivityLogout';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  useInactivityLogout();

  // -- State: Global/Auth --
  const [userInfo, setUserInfo] = useState(null);
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState(null);
  const [activeTab, setActiveTab] = useState('documents');

  // -- State: Municipalities (ProviderAdmin) --
  const [municipalitiesList, setMunicipalitiesList] = useState([]);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);
  const [municipalityWidgets, setMunicipalityWidgets] = useState({});

  // -- State: Theme --
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('adminDashboardTheme');
    return (saved === 'light' || saved === 'dark') ? saved : 'light';
  });

  // -- State: Cross-tab communication (Monitoring -> Testumgebung) --
  const [initialTestInput, setInitialTestInput] = useState('');

  // -- Init --
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const user = getUserFromToken();
    
    if (user?.must_change_password) {
      navigate('/change-password');
      return;
    }
    
    setUserInfo(user);
    if (user?.role !== 'ProviderAdmin' && user?.municipality_id) {
      setSelectedMunicipalityId(user.municipality_id);
    }
  }, [navigate]);

  // -- Theme Toggle --
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('adminDashboardTheme', newTheme);
  };

  // -- Fetch Municipalities (ProviderAdmin) --
  const fetchMunicipalities = async () => {
    try {
      setLoadingMunicipalities(true);
      const response = await apiClient.get('/provider/municipalities');
      const municipalitiesData = Array.isArray(response.data) ? response.data : [response.data];
      setMunicipalitiesList(municipalitiesData);

      const widgetsMap = {};
      for (const muni of municipalitiesData) {
        try {
          const widgetsResponse = await apiClient.get(`/provider/municipalities/${muni.id}/widgets`);
          widgetsMap[muni.id] = Array.isArray(widgetsResponse.data) ? widgetsResponse.data : [];
        } catch (err) {
          console.error(`Error fetching widgets for ${muni.id}:`, err);
          widgetsMap[muni.id] = [];
        }
      }
      setMunicipalityWidgets(widgetsMap);
    } catch (error) {
      console.error('Error fetching municipalities:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Unbekannter Fehler';
      
      if (error.response?.status === 403) {
        // silently ignore for non-providers
      } else if (error.response?.status === 404) {
        setMunicipalitiesList([]);
        setMunicipalityWidgets({});
      } else {
        // toast removed - tab components handle their own errors
      }
    } finally {
      setLoadingMunicipalities(false);
    }
  };

  useEffect(() => {
    if (userInfo?.role === 'ProviderAdmin') {
      fetchMunicipalities();
    }
  }, [userInfo]);

  // -- Cross-tab handler: Monitoring -> Testumgebung --
  const handleOpenInTestumgebung = (question) => {
    setInitialTestInput(question);
    setActiveTab('testumgebung');
  };

  const isDark = theme === 'dark';
  
  return (
    <div className={`min-h-screen p-6 md:p-8 font-sans transition-colors ${
      isDark 
        ? 'bg-slate-950 text-slate-50' 
        : 'bg-slate-50 text-slate-900'
    }`}>
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold tracking-tight flex items-center gap-2 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              <Shield className="w-6 h-6 text-sky-400" />
              Admin Dashboard
            </h1>
            <p className={`mt-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Verwaltung für {userInfo?.municipality_id ? 'Ihre Gemeinde' : 'StadtKI'}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
             <div className="text-sm text-right mr-4 hidden md:block">
                <div className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{userInfo?.full_name}</div>
                <div className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{userInfo?.email}</div>
             </div>
             <Button 
               variant="outline" 
               size="icon"
               className={isDark 
                 ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white" 
                 : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
               }
               onClick={toggleTheme}
               title={isDark ? 'Zu hellem Modus wechseln' : 'Zu dunklem Modus wechseln'}
             >
               {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
             </Button>
             <Button 
               variant="outline" 
               className={isDark
                 ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white"
                 : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
               } 
               onClick={() => {
                 localStorage.removeItem('token');
                 navigate('/login');
               }}
             >
               Logout
             </Button>
          </div>
        </div>

        {/* Global Municipality Filter (ProviderAdmin) */}
        {userInfo?.role === 'ProviderAdmin' && (
          <div className={`flex items-center gap-3 p-4 rounded-lg border ${
            isDark 
              ? 'bg-slate-900/50 border-slate-800' 
              : 'bg-white border-slate-200'
          }`}>
            <Building className={`w-4 h-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`} />
            <span className={`text-sm ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>Mandant filtern:</span>
            <Select 
              value={selectedMunicipalityId || 'all'} 
              onValueChange={(val) => setSelectedMunicipalityId(val === 'all' ? null : val)}
            >
              <SelectTrigger className={`w-[250px] ${
                isDark 
                  ? 'bg-slate-950 border-slate-700 text-white' 
                  : 'bg-white border-slate-300 text-slate-900'
              }`}>
                <SelectValue placeholder="Alle Gemeinden" />
              </SelectTrigger>
              <SelectContent className={isDark 
                ? 'bg-slate-900 border-slate-700 text-white' 
                : 'bg-white border-slate-300 text-slate-900'
              }>
                <SelectItem value="all">Alle Gemeinden</SelectItem>
                {municipalitiesList.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name || m.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={isDark 
            ? "bg-slate-900 border border-slate-800 text-slate-400" 
            : "bg-white border border-slate-200 text-slate-600"
          }>
            <TabsTrigger value="documents" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white">
              Dokumente
            </TabsTrigger>
            {(userInfo?.role === 'ProviderAdmin' || userInfo?.role === 'MunicipalityAdmin') && (
              <TabsTrigger value="users" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white">
                Benutzer
              </TabsTrigger>
            )}
            {(userInfo?.role === 'ProviderAdmin' || userInfo?.role === 'MunicipalityAdmin') && (
              <TabsTrigger value="widget-config" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white">
                Widget-Einstellungen
              </TabsTrigger>
            )}
            {(userInfo?.role === 'ProviderAdmin' || userInfo?.role === 'MunicipalityAdmin') && (
              <TabsTrigger value="sitemap" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white">
                Sitemap
              </TabsTrigger>
            )}
            {userInfo?.role === 'ProviderAdmin' && (
              <TabsTrigger value="landesrecht" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white">
                Landesrecht
              </TabsTrigger>
            )}
            {(userInfo?.role === 'ProviderAdmin' || userInfo?.role === 'MunicipalityAdmin') && (
              <TabsTrigger value="monitoring" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white">
                Monitoring
              </TabsTrigger>
            )}
            {(userInfo?.role === 'ProviderAdmin' || userInfo?.role === 'MunicipalityAdmin') && (
              <TabsTrigger value="testumgebung" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white">
                Testumgebung
              </TabsTrigger>
            )}
            {userInfo?.role === 'ProviderAdmin' && (
              <TabsTrigger value="quality" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white">
                Qualität
              </TabsTrigger>
            )}
            {userInfo?.role === 'ProviderAdmin' && (
              <TabsTrigger value="municipalities" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white">
                Gemeinden
              </TabsTrigger>
            )}
          </TabsList>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-4">
            <DokumenteTab
              isDark={isDark}
              userInfo={userInfo}
              selectedMunicipalityId={selectedMunicipalityId}
              municipalitiesList={municipalitiesList}
            />
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users" className="space-y-4">
            <BenutzerTab
              isDark={isDark}
              userInfo={userInfo}
              selectedMunicipalityId={selectedMunicipalityId}
              municipalitiesList={municipalitiesList}
            />
          </TabsContent>

          {/* WIDGET CONFIG TAB */}
          <TabsContent value="widget-config" className="space-y-4">
            <WidgetConfigTab
              isDark={isDark}
              userInfo={userInfo}
              selectedMunicipalityId={selectedMunicipalityId}
            />
          </TabsContent>

          {/* SITEMAP TAB */}
          {(userInfo?.role === 'ProviderAdmin' || userInfo?.role === 'MunicipalityAdmin') && (
            <TabsContent value="sitemap" className="space-y-4">
              <SitemapTab
                isDark={isDark}
                userInfo={userInfo}
                selectedMunicipalityId={selectedMunicipalityId}
                municipalitiesList={municipalitiesList}
              />
            </TabsContent>
          )}

          {/* LANDESRECHT TAB (ProviderAdmin only) */}
          {userInfo?.role === 'ProviderAdmin' && (
            <TabsContent value="landesrecht" className="space-y-4">
              <LandesrechtTab isDark={isDark} />
            </TabsContent>
          )}

          {/* MONITORING TAB (Admin) */}
          {(userInfo?.role === 'ProviderAdmin' || userInfo?.role === 'MunicipalityAdmin') && (
            <TabsContent value="monitoring" className="space-y-4">
              <MonitoringTab
                isDark={isDark}
                userInfo={userInfo}
                selectedMunicipalityId={selectedMunicipalityId}
                onOpenInTestumgebung={handleOpenInTestumgebung}
              />
            </TabsContent>
          )}

          {/* TESTUMGEBUNG TAB (Admin) */}
          {(userInfo?.role === 'ProviderAdmin' || userInfo?.role === 'MunicipalityAdmin') && (
            <TabsContent value="testumgebung" className="space-y-4">
              <TestumgebungTab
                isDark={isDark}
                userInfo={userInfo}
                selectedMunicipalityId={selectedMunicipalityId}
                municipalitiesList={municipalitiesList}
                initialTestInput={initialTestInput}
              />
            </TabsContent>
          )}

          {/* QUALITY TAB (ProviderAdmin only) */}
          {userInfo?.role === 'ProviderAdmin' && (
            <TabsContent value="quality" className="space-y-4">
              <QualitaetTab
                isDark={isDark}
                userInfo={userInfo}
                selectedMunicipalityId={selectedMunicipalityId}
                municipalitiesList={municipalitiesList}
              />
            </TabsContent>
          )}

          {/* MUNICIPALITIES TAB (ProviderAdmin only) */}
          {userInfo?.role === 'ProviderAdmin' && (
            <TabsContent value="municipalities" className="space-y-4">
              <GemeindenTab
                isDark={isDark}
                municipalitiesList={municipalitiesList}
                loadingMunicipalities={loadingMunicipalities}
                municipalityWidgets={municipalityWidgets}
                fetchMunicipalities={fetchMunicipalities}
              />
            </TabsContent>
          )}

        </Tabs>

      </div>
    </div>
  );
}

export default AdminDashboard;
